import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import adminService, { type TeamPosition } from '../../services/adminService';

/**
 * Granting somebody a staff role, with the position they hold on the team.
 *
 * A modal rather than a confirm dialog because this takes an answer, not a yes.
 *
 * The positions come from the database. They used to come from a hand-written copy of the
 * backend enum in this repo, which meant adding one needed a frontend deploy and the two
 * lists could drift apart without anybody noticing. Only active positions are offered:
 * retired ones stay on the people who already hold them but are not handed out again.
 *
 * Deliberately not the member craft list. A craft is a skill somebody declares about
 * themselves on their community profile; a position is a job an admin assigns. The two
 * overlap in wording and mean different things.
 */

interface StaffRoleModalProps {
  isOpen: boolean;
  personLabel: string;
  initialPosition?: string | null;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (position: string) => void;
}

const StaffRoleModal: React.FC<StaffRoleModalProps> = ({
  isOpen, personLabel, initialPosition, loading = false, onCancel, onConfirm,
}) => {
  const [positions, setPositions] = useState<TeamPosition[]>([]);
  const [position, setPosition] = useState(initialPosition || '');
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadPositions = useCallback(async () => {
    setLoadingPositions(true);
    setLoadError(null);
    try {
      const list = await adminService.getTeamPositions();
      setPositions(list);
      // Keep the position they already hold selected even if it has since been retired:
      // the list would not contain it, and silently moving them to a different job
      // because a dropdown had no matching option would be worse than offering it.
      setPosition((current) => current || list[0]?.name || '');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load the positions');
    } finally {
      setLoadingPositions(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      void loadPositions();
    }
  }, [isOpen, loadPositions]);

  if (!isOpen) return null;

  // Nothing to choose from means nothing honest to submit. Granting a staff role with a
  // position the server will refuse would report success and change nothing.
  const canConfirm = !loading && !loadingPositions && !loadError && position !== '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Give {personLabel} a staff role</h3>
          <button type="button" onClick={onCancel} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* What staff can and cannot do, because "staff" on its own says nothing about
            how much access is being handed over. */}
        <p className="mb-4 text-sm text-gray-600">
          They will see the admin panel and be able to approve community members, and create
          and edit casting calls and productions. They will <strong>not</strong> be able to
          create or change content, manage users, moderate comments, or send notifications.
        </p>

        <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor="staff-position">
          Position on the team
        </label>

        {loadingPositions && (
          <p data-testid="positions-loading" className="mb-6 text-sm text-gray-500">
            Loading positions…
          </p>
        )}

        {loadError && !loadingPositions && (
          <div data-testid="positions-error" className="mb-6 rounded-md bg-red-50 p-3 text-sm text-red-800">
            <p>{loadError}</p>
            <button
              type="button"
              onClick={() => void loadPositions()}
              className="mt-2 font-medium underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {!loadingPositions && !loadError && (
          <select
            id="staff-position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="mb-6 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500"
          >
            {/* Their current position, when it is no longer on offer. Shown so the modal
                does not appear to move them to a job nobody chose. */}
            {initialPosition && !positions.some((p) => p.name === initialPosition) && (
              <option value={initialPosition}>{initialPosition} (retired)</option>
            )}
            {positions.map((p) => (
              <option key={p.name} value={p.name}>{p.label}</option>
            ))}
          </select>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(position)}
            disabled={!canConfirm}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Grant staff role'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffRoleModal;
